import { Entity } from "@thewebops/admin-panel";
import moment from "moment";
import actions from "./actions";
import reducers from "./reducers";
import sagas from "./sagas";
import { MESSAGE_EXPORT_TYPE, MESSAGE_EXPORT_RETURNED_TYPE } from "./constants";
import { authConfig } from "authEntities";

const formatFilterDate = (date) => (date ? moment(date).format("DD-MM-YYYY") : null);

const messageExportName = ({ filters }) => {
  const from = formatFilterDate(filters?.from);
  const to = formatFilterDate(filters?.to);
  if (from && to) return `Messages ${from} to ${to}`;
  if (from) return `Messages from ${from}`;
  if (to) return `Messages until ${to}`;
  return "All messages";
};

const exportMessagesAction = {
  label: "Export messages",
  name: "exportMessages",
  requireConfirmation: true,
  confirmationMessage: "Export all chat messages to a spreadsheet? It is prepared in the background.",
};

// The same admins who can read the messages (the API answers 403 to the others).
const MESSAGE_EXPORT_ROLES = ["super_admin", "observer"];

// The signed-in admin, as the panel keeps it in localStorage under authConfig.name ("admin").
const currentAdminRole = () => {
  try {
    return JSON.parse(localStorage.getItem(authConfig.name))?.role;
  } catch (error) {
    return undefined;
  }
};

const exportDocument = new Entity({
  name: "exportDocument",
  pluralName: "exportDocuments",
  pathName: "export_documents",
  endpoint: "/admin/export_documents",
  serverName: "export_document",
  serverPluralName: "export_documents",
  displayName: "Export Documents",
  allowDelete: true,
  showRowActions: true,
  customActions: actions,
  customReducers: reducers,
  customSagas: sagas,
  requestParams: {
    list: {
      includes: 'exportable',
    },
  },
  fields: [
    {
      identifier: "id",
      label: "ID",
      accessor: "id",
      inList: true,
      usedToShow: true,
    },
    {
      identifier: "status",
      label: "Status",
      accessor: "status",
      type: "string",
      inList: true,
    },
    {
      identifier: "created_at",
      label: "Created At",
      accessor: dataObject => moment(dataObject.created_at).format("DD-MM-YYYY hh:mm A"),
      inList: true,
      type: "string",
    },
    {
      identifier: "updated_at",
      label: "Updated At",
      accessor: dataObject => moment(dataObject.updated_at).format("DD-MM-YYYY hh:mm A"),
      inList: true,
      type: "string",
    },
    {
      identifier: "exportable_type",
      label: "Document Type",
      accessor: "exportable_type",
      inList: true,
      type: "string",
    },
    {
      identifier: "exportable_name",
      label: "Document name",
      accessor: (dataObject) =>
        dataObject?.exportable_type === MESSAGE_EXPORT_RETURNED_TYPE ? messageExportName(dataObject) : dataObject?.exportable?.name,
      inList: true,
      type: "string",
    },
    {
      identifier: "error_message",
      label: "Error",
      // Empty, not the text "null", for an export without an error.
      accessor: (dataObject) => dataObject?.error_message || "",
      inList: true,
      type: "string",
    },
    {
      identifier: "file",
      label: "Download link",
      // Empty, not the text "null", for an export that has no file yet or failed (#70).
      accessor: (dataObject) => dataObject?.file_url || "",
      type: "file",
      inList: true,
      hideInShow: true,
    },
  ],
  filters: [
    {
      label: "Document Type",
      name: "exportable_type",
      type: "select",
      selectConfig: {
        optionsType: "static",
        optionsArray: [
          { label: "Habit", value: "Habit" },
          { label: "Nutibite", value: "Lesson" },
          { label: "Exercise", value: "Exercise" },
          { label: "Message", value: MESSAGE_EXPORT_TYPE },
        ],
      },
    },
  ],
});

// admin-panel 0.1.59 checks a page button's roles under rowActions instead of globalActions, so
// authorizedRoles on a global action is ignored. The toolbar reads globalActions on every render,
// so the button is only listed for the admins who may use it.
Object.defineProperty(exportDocument, "globalActions", {
  get: () => (MESSAGE_EXPORT_ROLES.includes(currentAdminRole()) ? [exportMessagesAction] : []),
});

export default exportDocument;
